// What a package draft holds, and the one decision about it that is
// logic rather than plumbing (Documents tab PR 8c, "Save as draft").
//
// CONSUMER: PrepareAndSend.tsx (writes and reads it back), DocumentsTab
// and PackageWizard (open and start drafts). The server stores the state
// as one opaque JSON value (shared/db/esign-package-drafts.ts) and never
// reads inside it beyond refusing an encrypted-field binding.
//
// Versioned so a later screen can still open a draft an older one saved:
// a field the screen gains later is simply absent from an older draft,
// and the screen's own default stands in for it.

import type { Casting } from "@shared/esign/role-casting";
import { isGhost } from "@shared/esign/ghost-offers";

import type { SigningVideo } from "@shared/esign/signing-email-extras";

import type { ForwardRecipient } from "./forward-recipient";
import type { PrepField, PrepStamp } from "./prepare-helpers";
import type { ReminderUnit } from "./reminder-cadence";

/** One document's saved work. */
export interface DraftDocumentWork {
  fields: PrepField[];
  stamps: PrepStamp[];
  fingerprint: string | null;
}

export interface EsignDraftStateV1 {
  v: 1;
  /** Saved from step 1, before the send screen was ever opened: only the
   *  documents matter, and the send screen opens on them fresh. */
  startedOnly?: boolean;
  /** The company a standalone (household) send was set to. */
  companySlug: string | null;
  /** Only documents someone worked on (see worthKeeping). A document
   *  absent here is read afresh when the draft opens, exactly as a new
   *  send reads it. */
  work: Record<string, DraftDocumentWork>;
  chosenTypeByDoc: Record<string, string>;
  confirmedPagesByDoc: Record<string, number[]>;
  guests: { name: string; email: string }[];
  selected: string[];
  copyOnly: string[];
  casting: Casting | null;
  enforceOrder: boolean;
  /** Each signer's number in the signing order (PR 8d); an older draft
   *  has none, and its order is the list order, as it was. */
  orderByKey: Record<string, number>;
  remind: { amount: string; unit: ReminderUnit; times: string };
  expiryDays: number | null;
  message: string;
  /** Null means the default subject, recomputed when the draft opens. */
  subject: string | null;
  video: SigningVideo | null;
  /** The email's lines changed for this send (PR 8f); null is the
   *  library's wording, and what an older draft opens with. */
  emailBody: string | null;
  forwardTo: ForwardRecipient[];
}

/**
 * Whether a document's fields are worth saving. A document nobody has
 * touched holds only the suggestions the screen offered on its own; those
 * come back identically when the draft is opened and the document is read
 * again, so saving them would only make the draft large. Anything a person
 * did (accepted, placed, typed, stamped) makes it worth keeping, and then
 * the whole document is kept, suggestions included, so it reopens exactly
 * as it was left.
 */
export function worthKeeping(work: { fields: readonly PrepField[]; stamps: readonly PrepStamp[] }): boolean {
  return work.stamps.length > 0 || work.fields.some((f) => !isGhost(f));
}

/** A saved state, or null when it isn't one this screen can open. */
export function readDraftState(raw: unknown): EsignDraftStateV1 | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Partial<EsignDraftStateV1>;
  if (s.v !== 1) return null;
  return {
    v: 1,
    ...(s.startedOnly === true ? { startedOnly: true } : {}),
    companySlug: typeof s.companySlug === "string" ? s.companySlug : null,
    work: s.work && typeof s.work === "object" ? s.work : {},
    chosenTypeByDoc: s.chosenTypeByDoc && typeof s.chosenTypeByDoc === "object" ? s.chosenTypeByDoc : {},
    confirmedPagesByDoc:
      s.confirmedPagesByDoc && typeof s.confirmedPagesByDoc === "object" ? s.confirmedPagesByDoc : {},
    guests: Array.isArray(s.guests) ? s.guests : [],
    selected: Array.isArray(s.selected) ? s.selected : [],
    copyOnly: Array.isArray(s.copyOnly) ? s.copyOnly : [],
    casting: Array.isArray(s.casting) ? s.casting : null,
    enforceOrder: s.enforceOrder === true,
    orderByKey: s.orderByKey && typeof s.orderByKey === "object" ? s.orderByKey : {},
    remind:
      s.remind && typeof s.remind === "object"
        ? s.remind
        : { amount: "2", unit: "days", times: "0" },
    expiryDays: typeof s.expiryDays === "number" ? s.expiryDays : null,
    message: typeof s.message === "string" ? s.message : "",
    subject: typeof s.subject === "string" ? s.subject : null,
    video: s.video && typeof s.video === "object" ? s.video : null,
    emailBody: typeof s.emailBody === "string" ? s.emailBody.slice(0, 4000) : null,
    forwardTo: Array.isArray(s.forwardTo) ? s.forwardTo : [],
  };
}
